import {
  toStyleLanguageMatchKey,
  type AzureTranslationEntry,
  type StyleLanguageOption,
} from './styleLanguageMatch.ts';
import {
  getCustomStylePreset,
  normalizeCustomStylePresets,
} from './customStylePresets.ts';
import { normalizeStyleLanguagePriorities } from './styleLanguagePriorities.ts';

/**
 * Azure translation codes excluded from Style Studio auto-apply language UI.
 * Matching still uses toStyleLanguageMatchKey; these simply never appear as bind targets.
 */
export const STYLE_STUDIO_EXCLUDED_AZURE_CODES = new Set([
  'lzh', // Chinese Literary / 文言文
  'yue', // Cantonese / 粵語
]);

type Candidate = {
  code: string;
  matchKey: string;
  name: string;
  nativeName: string;
};

function isPreferredCandidate(next: Candidate, current: Candidate): boolean {
  const nextExact = next.code.toLowerCase() === next.matchKey.toLowerCase();
  const currentExact = current.code.toLowerCase() === current.matchKey.toLowerCase();
  if (nextExact !== currentExact) return nextExact;

  const nextGeneric = !next.name.includes('(');
  const currentGeneric = !current.name.includes('(');
  if (nextGeneric !== currentGeneric) return nextGeneric;

  if (next.name.length !== current.name.length) {
    return next.name.length < current.name.length;
  }
  return next.name.localeCompare(current.name, 'en') < 0;
}

function toOption(candidate: Candidate): StyleLanguageOption {
  return {
    matchKey: candidate.matchKey,
    name: candidate.name,
    nativeName: candidate.nativeName,
    label: `${candidate.name} (${candidate.nativeName})`,
  };
}

/**
 * Curated Style Studio language list from Azure translation catalog:
 * exclude literary Chinese / Cantonese, merge same primary (matchKey), sort A–Z by English name.
 */
export function buildStyleStudioLanguageOptions(
  azureTranslation: Record<string, AzureTranslationEntry>,
): StyleLanguageOption[] {
  const best = new Map<string, Candidate>();

  for (const code of Object.keys(azureTranslation)) {
    if (STYLE_STUDIO_EXCLUDED_AZURE_CODES.has(code)) continue;
    const entry = azureTranslation[code];
    if (!entry) continue;
    const matchKey = toStyleLanguageMatchKey(code);
    if (!matchKey) continue;

    const candidate: Candidate = {
      code,
      matchKey,
      name: entry.name || code,
      nativeName: entry.nativeName || entry.name || code,
    };
    const existing = best.get(matchKey);
    if (!existing || isPreferredCandidate(candidate, existing)) {
      best.set(matchKey, candidate);
    }
  }

  return [...best.values()]
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

export function filterAndOrderStyleLanguageOptions(
  options: readonly StyleLanguageOption[],
  selectedKeys: ReadonlySet<string>,
  query: string,
): StyleLanguageOption[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((opt) => {
      const hay = `${opt.name} ${opt.nativeName} ${opt.matchKey} ${opt.label}`.toLowerCase();
      return hay.includes(q);
    })
    : [...options];

  return filtered.sort((a, b) => {
    const aSel = selectedKeys.has(a.matchKey) ? 0 : 1;
    const bSel = selectedKeys.has(b.matchKey) ? 0 : 1;
    if (aSel !== bSel) return aSel - bSel;
    return a.name.localeCompare(b.name, 'en');
  });
}

export type StyleLanguageBindingHint = {
  styleName: string;
  languageLabels: string[];
};

/** Group language→style priorities for popup / settings hints. */
export function buildStyleLanguageBindingHints(
  prioritiesRaw: unknown,
  presetsRaw: unknown,
  langOptions: readonly StyleLanguageOption[],
): StyleLanguageBindingHint[] {
  const presets = normalizeCustomStylePresets(presetsRaw);
  const priorities = normalizeStyleLanguagePriorities(prioritiesRaw, presets);
  const labelByKey = new Map(langOptions.map((opt) => [opt.matchKey, opt.label]));
  const byPreset = new Map<string, { styleName: string; labels: string[] }>();

  for (const [matchKey, presetId] of Object.entries(priorities)) {
    const preset = getCustomStylePreset(presets, presetId);
    if (!preset) continue;
    const row = byPreset.get(presetId) ?? { styleName: preset.name, labels: [] };
    row.labels.push(labelByKey.get(matchKey) || matchKey);
    byPreset.set(presetId, row);
  }

  return [...byPreset.values()]
    .map((row) => ({
      styleName: row.styleName,
      languageLabels: [...row.labels].sort((a, b) => a.localeCompare(b, 'en')),
    }))
    .sort((a, b) => a.styleName.localeCompare(b.styleName, 'en'));
}

